import React, { useEffect, useState, useMemo } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import Swal from "sweetalert2";

const IssuedCertificates = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all, merit, participation

  const load = async () => {
    try {
      setLoading(true);
      const res = await API.fetchIssuedCertificates();
      setCerts(res?.data || res || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.message || "Failed to load certificates", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRevoke = async (id) => {
    const { value: reason } = await Swal.fire({
      title: "Revoke certificate?",
      input: "text",
      inputPlaceholder: "Enter reason for revocation",
      showCancelButton: true,
      confirmButtonText: "Next",
      confirmButtonColor: "#3085d6",
      inputValidator: (v) => !v.trim() && "Revocation reason is required!"
    });
    if (reason === undefined || !reason.trim()) return;

    const { value: password } = await Swal.fire({
      title: "Confirm Password",
      input: "password",
      inputLabel: "Please enter your password to confirm revocation",
      inputPlaceholder: "Enter password",
      showCancelButton: true,
      confirmButtonText: "Revoke",
      confirmButtonColor: "#dc2626",
      inputValidator: (v) => !v && "Password is required!"
    });
    if (!password) return;

    try {
      await API.revokeCertificate(id, reason || "", password);
      await load();
      Swal.fire("Done", "Certificate revoked", "success");
    } catch (err) {
      console.error(err);
      const errMsg = err?.response?.data?.message || err?.message || "Failed to revoke";
      Swal.fire("Error", errMsg, "error");
    }
  };

  const handleDownload = async (regId, regenerate = false) => {
    try {
      if (!regId) {
        Swal.fire("Error", "No registration link found for this certificate", "error");
        return;
      }
      let keep_signatures = false;
      if (regenerate) {
        const { value: action } = await Swal.fire({
            title: 'Reprint Certificate',
            input: 'radio',
            inputOptions: {
                'correction': 'Correction (update data, keep old signatures)',
                'regenerate': 'Regenerate (update data, use current signatures)'
            },
            inputValue: 'correction',
            showCancelButton: true,
            confirmButtonText: 'Proceed',
            confirmButtonColor: '#0d3373',
        });
        if (!action) return;
        keep_signatures = action === 'correction';
      }

      Swal.fire({
        title: regenerate ? "Generating certificate..." : "Downloading certificate...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const { blob, filename } = await API.downloadRegistrationCertificate(regId, { regenerate, keep_signatures });
      Swal.close();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.close();
      const errMsg = err?.response?.data?.message || err?.message || "Failed to download certificate.";
      Swal.fire("Error", errMsg, "error");
    }
  };

  // Filter certificates
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  let filtered = certs;
  if (filter === "merit") {
    filtered = certs.filter(
      (c) => c.medal_type && c.medal_type !== "Participation"
    );
  } else if (filter === "participation") {
    filtered = certs.filter((c) => c.medal_type === "Participation");
  }

  const sortedCertificates = useMemo(() => {
    if (!sortConfig.key) return filtered;
    return [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";
      const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [filtered, sortConfig]);

  const meritCount = certs.filter(
    (c) => c.medal_type && c.medal_type !== "Participation"
  ).length;
  const participationCount = certs.filter(
    (c) => c.medal_type === "Participation"
  ).length;

  const getMedalEmoji = (medalType) => {
    if (medalType === "Gold") return "🥇";
    if (medalType === "Silver") return "🥈";
    if (medalType === "Bronze") return "🥉";
    return "📜";
  };

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-5 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Certificate Desk</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Issued Certificates</h1>
              <p className="text-sm text-slate-600 mt-1">
                Monitor issued records and revoke invalid certificates.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Total</p>
                <p className="text-lg font-semibold text-slate-900">{certs.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Merit</p>
                <p className="text-lg font-semibold text-slate-900">{meritCount}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Participation</p>
                <p className="text-lg font-semibold text-slate-900">{participationCount}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              filter === "all"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All ({certs.length})
          </button>
          <button
            onClick={() => setFilter("merit")}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              filter === "merit"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Merit Certificates ({meritCount})
          </button>
          <button
            onClick={() => setFilter("participation")}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              filter === "participation"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Participation Certificates ({participationCount})
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">
              {filter === "merit"
                ? "Merit Certificates"
                : filter === "participation"
                ? "Participation Certificates"
                : "All Certificates"}
            </h2>
            {loading && (
              <span className="text-xs text-gray-500 animate-pulse">
                Loading...
              </span>
            )}
          </div>

          {!filtered.length && !loading ? (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg">No certificates found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[1050px] w-full text-xs sm:text-sm divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 select-none">
                  <tr>
                    <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                      S.No.
                    </th>
                    {[
                      { label: "Medal", key: "medal_type" },
                      { label: "Player Name", key: "player_name" },
                      { label: "Event / Game", key: "event_title" },
                      { label: "Serial No", key: "serial_no" },
                      { label: "Issued At", key: "issued_at" },
                      { label: "Status", key: "revoked" },
                    ].map((col, idx) => (
                      <th
                        key={idx}
                        onClick={() => handleSort(col.key)}
                        className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200 transition"
                      >
                        <div className="flex items-center gap-1">
                          <span>{col.label}</span>
                          <span className="text-[10px] text-gray-400">
                            {sortConfig.key === col.key
                              ? sortConfig.direction === "asc"
                                ? "▲"
                                : "▼"
                              : "↕"}
                          </span>
                        </div>
                      </th>
                    ))}
                    <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {sortedCertificates.map((c, index) => (
                    <tr
                      key={c.id}
                      className="hover:bg-gray-50 transition duration-150"
                    >
                      <td className="px-3 py-2 text-center font-semibold">{index + 1}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-base">
                          {getMedalEmoji(c.medal_type)}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-gray-900">
                          {c.player_name}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        <div className="font-medium">{c.event_title}</div>
                        <div className="text-xs text-gray-500">
                          {c.game_name || c.sport_name}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm font-mono text-gray-600">
                        {c.serial_no}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">
                        {c.issued_at
                          ? new Date(c.issued_at).toLocaleString("en-IN")
                          : "-"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {c.revoked ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-semibold">
                            Revoked
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-semibold">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          {!c.revoked && c.registration_id && (
                            <>
                              <button
                                onClick={() => handleDownload(c.registration_id, false)}
                                className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-800 text-[11px] font-semibold rounded-lg transition shadow-sm"
                              >
                                Download
                              </button>
                              <button
                                onClick={() => handleDownload(c.registration_id, true)}
                                className="px-2.5 py-1 bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-semibold rounded-lg transition shadow-sm"
                              >
                                Reprint
                              </button>
                            </>
                          )}
                          {!c.revoked && (
                            <button
                              onClick={() => handleRevoke(c.id)}
                              className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white text-[11px] font-semibold rounded-lg transition shadow-sm"
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default IssuedCertificates;
